import axios from 'axios';
import { MDBTreeview, MDBTreeviewItem } from 'mdb-react-treeview';
import { MDBBtn, MDBBtnGroup, MDBCol, MDBContainer, MDBIcon, MDBInput, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalFooter, MDBModalHeader, MDBRow, MDBSelect, MDBTooltip, } from 'mdb-react-ui-kit';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { UserProfileContext } from '../../context/UserProfileProvider';

export default function GroupUserManager() {
    const [groupsFull, setGroupsFull] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [groupDn, setGroupDn] = useState(null);
    // State variables for updating DNs
const [currentDn, setCurrentDn] = useState('');     // The DN being updated (userDn or groupDn)
const [updatedValue, setUpdatedValue] = useState(''); // The new cn or uid value
const [updateType, setUpdateType] = useState('');   // 'cn' for group, 'uid' for user
const [originalDn, setOriginalDn] = useState('');   // The parent groupDn

const {groups } = useContext(UserProfileContext);
    const [allUsers, setAllUsers] = useState([]);
    const inventoryUrl = process.env.REACT_APP_INVENTORY_URL;
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [updateGroupName, setUpdateGroupName] = useState('');
    const [updateGroupBaseDn, setUpdateGroupBaseDn] = useState('');
    const [tempGroupName, setTempGroupName] = useState('');
    const [tempUsers, setTempUsers] = useState([]);
    const[oldUniqueMember, setOldUniqueMember] = useState();
    const [groupStructure, setGroupStructure] = useState({
        id: uuidv4(),
        groupName: '',
        groupDn: '',
        users: [],
        subGroups: []
    });


    const findGroupById = (group, groupId) => {
        if (group.id === groupId) return group;
        for (let subGroup of group.subGroups) {
            const found = findGroupById(subGroup, groupId);
            if (found) return found;
        }
        return null;
    };

    const [localGroupInputs, setLocalGroupInputs] = useState({});

    const extractUid = useCallback((dn) => {
        if (typeof dn === 'string') {
            const match = dn.match(/uid=([^,]+)/);
            return match ? match[1] : dn;
        }
        return '';
    }, []);


    const extractCn = (dn) => {
        const match = dn.match(/cn=([^,]+)/);
        return match ? match[1] : dn;
    };
    
    
    

    useEffect(() => {
        fetchGroupsAndUsers();
    }, []);

    const fetchGroupsAndUsers = async () => {
        try {
            const response = await axios.get(`${inventoryUrl}/users/ou/groups`);
            setGroupsFull(response.data || []);
            setAllUsers(getAllUniqueUsers(response.data || []));
        } catch (error) {
            console.error("Error fetching groups and users:", error);
        }
    };

    const getAllUniqueUsers = useCallback((groupsData) => {
        const usersSet = new Set();
        const extractUsers = (group) => {
            (group.users || []).forEach(user => usersSet.add(user));
            (group.subGroups || []).forEach(subGroup => extractUsers(subGroup));
        };
        groupsData.forEach(group => extractUsers(group));
        return Array.from(usersSet).map(userDn => ({ text: extractUid(userDn), value: userDn }));
    }, [extractUid]);

    const removeGroup = async (groupDn) => {
        try {
            await axios.delete(`${inventoryUrl}/users/groups`, { params: { groupDn } });
            fetchGroupsAndUsers();
        } catch (error) {
            console.error("Error removing group or user:", error);
        }
    };

    const removeTheUserRefFromGroup = async (groupDn, userDn) => {
        try {
            await axios.delete(`${inventoryUrl}/users/groups/user`, { params: { groupDn, userDn } });
            fetchGroupsAndUsers();
        } catch (error) {
            console.error("Error removing group or user:", error);
        }
    };

    const handleCreateGroup = async () => {
        // Build the full structure with all nested updates
        const updatedGroupStructure = saveAllGroups(groupStructure);
    
        try {
            await axios.post(`${inventoryUrl}/users/groups`, updatedGroupStructure);
            fetchGroupsAndUsers();
            setShowAddModal(false);
            setTempGroupName('');
            setTempUsers([]);
            setGroupStructure({
                id: uuidv4(),
                groupName: '',
                groupDn: '',
                users: [],
                subGroups: []
            });
        } catch (error) {
            console.error("Error creating group:", error);
        }
    };
    
    // Recursive function to apply all local inputs to the group structure
    const saveAllGroups = (group) => {
        const groupId = group.id;
        const localInputs = localGroupInputs[groupId];
        
        // Apply local inputs if they exist for this group
        const updatedGroup = {
            ...group,
            ...(localInputs ? { groupName: localInputs.groupName, users: localInputs.users } : {})
        };
    
        // Recursively update each subgroup
        updatedGroup.subGroups = group.subGroups.map(subGroup => saveAllGroups(subGroup));
    
        return updatedGroup;
    };
    

    const addSubgroup = (parentGroup) => {
        const newSubgroup = {
            id: uuidv4(),
            groupName: '',
            groupDn: parentGroup.groupDn,
            users: [],
            subGroups: []
        };
        parentGroup.subGroups.push(newSubgroup);
        setGroupStructure({ ...groupStructure });
    };


    const handleInputChange = (groupId, field, value) => {
        setLocalGroupInputs((prev) => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                [field]: value
            }
        }));
    };
    const handleUpdateDn = async () => {
        const updatedDn = updateDn(currentDn, updatedValue, updateType); // Update DN based on type ('cn' or 'uid')
    
        try {
            // Check if `cn` exists in the current DN
            const isGroupUpdate = updateType === "uid";
    
            const endpoint = isGroupUpdate ? `${inventoryUrl}/users/groups/members` : `${inventoryUrl}/users/groups`;
    
            // Prepare the params for the request
            const params = {
                oldUniqueMember: currentDn,
                newUniqueMember: updatedDn,
            };
    
            // Add `groupDn` param only if calling the `/groups/members` endpoint
            if (isGroupUpdate) {
                params.groupDn = originalDn; // Include parent group DN for members endpoint
            }
    
            // Make the API call
            await axios.put(endpoint, null, { params });
    
            console.log("DN updated successfully!");
            fetchGroupsAndUsers(); // Refresh groups and users after update
            setShowUpdateModal(false); // Close the modal
        } catch (error) {
            console.error("Error updating DN:", error);
        }
    };
    
    // Function to replace `cn` in groupDn or `uid` in userDn with a new value
    const updateDn = (dn, newValue, type) => {
        if (type === "cn") {
            //return dn.replace(/cn=[^,]+/, `cn=${newValue}`);
            return newValue;
        } else if (type === "uid") {
            return dn.replace(/uid=[^,]+/, `uid=${newValue}`);
        }
        return dn;
    };
    
    
    const openAddGroupModal = (group) => {
        // Set groupStructure with the selected group's groupDn
        setGroupDn(group.groupDn);
        setGroupStructure(prevStructure => ({
            ...prevStructure,
            groupDn: group.groupDn // Set the groupDn from the selected group
        }));
        setShowAddModal(true); // Open the modal
    };


    const addUsersToGroup = async (groupDn, userDns) => {
        try {
            const response = await axios.put(`${inventoryUrl}/users/groups/addUsers`, userDns, {
                params: { groupDn },
            });
            console.log("Users added to group successfully:", response.data);
            fetchGroupsAndUsers(); // Refresh group data
        } catch (error) {
            console.error("Error adding users to group:", error);
        }
    };
    const [showAddUsersModal, setShowAddUsersModal] = useState(false);
const [selectedGroupDn, setSelectedGroupDn] = useState(null);
const [selectedUsers, setSelectedUsers] = useState([]);

const openAddUsersModal = (groupDn) => {
    setSelectedGroupDn(groupDn);
    setSelectedUsers([]); // Clear previously selected users
    setShowAddUsersModal(true);
};

const handleAddUsers = async () => {
    if (selectedGroupDn && selectedUsers.length > 0) {
        await addUsersToGroup(selectedGroupDn, selectedUsers);
        setShowAddUsersModal(false);
    }
};


    const renderTree = (group, isFirstGroup = false) => (
        <MDBTooltip title={group.groupDn} key={`${group.groupDn}`} tag="span" placement="right">
            <MDBTreeviewItem key={group.groupDn} name={group.groupName} subtree show={isFirstGroup}>
                <MDBBtnGroup className="align-items-center mb-2">
                    <MDBBtn color="success" size="xs" onClick={() => openAddGroupModal(group)}>
                    <MDBIcon fas icon="plus-circle" />Group
                    </MDBBtn>
                  {group.groupName !== 'groups' &&  <> 
                  
                  {/* <MDBBtn color="warning" size="xs" onClick={() => {
                        setGroupDn(group.groupDn);
                        setShowUpdateModal(true);
                    }}>
                        Update
                    </MDBBtn> */}
                    <MDBBtn size="xs" color="warning" onClick={() => {
                    setOriginalDn(group.groupDn);
                    setCurrentDn(group.groupDn);
                    setUpdatedValue(extractCn(group.groupDn));
                    setUpdateType('cn');
                    setShowUpdateModal(true);
                }}>
                   <MDBIcon fas icon="edit" />Group
                </MDBBtn>
                   {group?.subGroups &&  <MDBBtn color="danger" size="xs" onClick={() => removeGroup(group.groupDn)}>
                    <MDBIcon fas icon="minus-square" />Group
                    </MDBBtn> }
                    <MDBBtn color="primary" size="xs" onClick={() => openAddUsersModal(group.groupDn)}>
                    <MDBIcon fas icon="plus-square" />Users
                </MDBBtn>
                    </>}
                </MDBBtnGroup>
                {group.users && group.users.map((userDn, index) => (
                    <MDBTooltip title={userDn} key={`${group.groupDn}-user-${index}`} tag="span" placement="right">
                        {group.groupName === 'groups' ?
                            <p className='mb-0'>
                                {extractUid(userDn)}
                            </p> :
                            <MDBTreeviewItem subtree name={`User: ${extractUid(userDn)}`}>
                             <MDBBtnGroup><MDBBtn color="danger" size="sm" onClick={() => removeTheUserRefFromGroup(group.groupDn, userDn)}>
                             <MDBIcon fas icon="minus-square" />User
                                </MDBBtn>            
                    {/* <MDBBtn color="warning" onClick={() => {
                    setOriginalDn(userDn);
                    setCurrentDn(userDn);
                    setUpdatedValue(extractUid(userDn));
                    setUpdateType('uid');
                    setShowUpdateModal(true);
                }}>
                    Update User
                </MDBBtn> */}
    
                               </MDBBtnGroup>   

                            </MDBTreeviewItem>
                        }
                    </MDBTooltip>
                ))}
                {group.subGroups && group.subGroups.map(subGroup => renderTree(subGroup))}
            </MDBTreeviewItem>
        </MDBTooltip>
    );

    const renderGroupInputs = (group) => {
        const groupId = group.id;
        const localInputs = localGroupInputs[groupId] || {
            groupName: group.groupName,
            groupDn: group.groupDn,
            users: group.users
        };

        return (
            <div
                style={{
                    marginLeft: '20px',
                    marginBottom: '10px',
                    padding: '10px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px'
                }}
                key={groupId}
            >
                <MDBRow style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                    <MDBCol>
                        <MDBInput
                            label="Group Name"
                            value={localInputs.groupName}
                            onChange={(e) =>{ handleInputChange(groupId, 'groupName', e.target.value);handleInputChange(groupId, 'groupDn', group.groupDn) }}
                        />
                    </MDBCol>
                    <MDBCol>
                        <MDBSelect
                            label="Select Users"
                            data={allUsers}
                            multiple
                            selected={localInputs.users}
                            onValueChange={(values) => handleInputChange(groupId, 'users', values.map((item) => item.value))}
                        />
                    </MDBCol>
                    <MDBCol>
                        <MDBBtn color="primary" size="sm" onClick={() => addSubgroup(group)}>
                        <MDBIcon fas icon="plus-circle" />Subgroup
                        </MDBBtn>
                    </MDBCol>
                </MDBRow>
                {group.subGroups.map((subGroup, subIndex) => (
                    <div
                        key={subGroup.id}
                        style={{
                            marginLeft: '20px',
                            paddingLeft: '10px',
                            borderLeft: '2px solid #d3d3d3'
                        }}
                    >
                        {renderGroupInputs(subGroup, subIndex)}
                    </div>
                ))}
            </div>
        );
    };
  
    
    return (
        <MDBContainer  className="justify-content-center align-items-center">
            
            <MDBTreeview selectable>
                {groupsFull.map((group, index) => renderTree(group, index === 0))}
            </MDBTreeview>
            <MDBModal open={showAddModal} setShow={setShowAddModal} tabIndex="-1" size="lg">
                <MDBModalDialog size="lg">
                    <MDBModalContent>
                        <MDBModalHeader>Add New Group</MDBModalHeader>
                        <MDBModalBody>
                            {renderGroupInputs(groupStructure)}
                        </MDBModalBody>
                        <MDBModalFooter>
                            <MDBBtn color="secondary" onClick={() => setShowAddModal(false)}>Close</MDBBtn>
                            <MDBBtn color="primary" onClick={handleCreateGroup}>Create</MDBBtn>
                        </MDBModalFooter>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
            
            <MDBModal open={showUpdateModal} setShow={setShowUpdateModal} tabIndex="-1" size="lg" closeOnEsc={(e)=>{    e.preventDefault(); setShowAddModal(false)}}>
    <MDBModalDialog>
        <MDBModalContent>
            <MDBModalHeader>{updateType === 'cn' ? "Update Group" : "User"}</MDBModalHeader>
            <MDBModalBody>
                <MDBInput className='mb-4'
                    label={updateType === 'cn' ? "Group Name (cn)" : "User Name (uid)"}
                    value={updatedValue}
                    onChange={(e) => setUpdatedValue(e.target.value)}
                />
                <MDBInput className='mb-4'
                    label="Original DN"
                    value={originalDn}
                    disabled
                />
                {/* <MDBSelect
  responsive
  className="mb-4"
  wrapperClass="custom-dropdown"
  label="Group"
  data={groups} // Ensure `transformedGroups` contains valid text and value
  search
  value={updatedValue}
  onValueChange={(value) => {
    setUpdatedValue(value?.value)
  }}
/> */}
            </MDBModalBody>
            <MDBModalFooter>
                <MDBBtn color="secondary" onClick={() => setShowUpdateModal(false)}>Close</MDBBtn>
                <MDBBtn color="primary" onClick={handleUpdateDn}>Update</MDBBtn>
            </MDBModalFooter>
        </MDBModalContent>
    </MDBModalDialog>
</MDBModal>

<MDBModal open={showAddUsersModal} setShow={setShowAddUsersModal} tabIndex="-1" size="lg">
    <MDBModalDialog>
        <MDBModalContent>
            <MDBModalHeader>Add Users to Group</MDBModalHeader>
            <MDBModalBody>
                <MDBSelect
                    label="Select Users"
                    data={allUsers}
                    multiple
                    selected={selectedUsers}
                    onValueChange={(values) => setSelectedUsers(values.map((item) => item.value))}
                />
            </MDBModalBody>
            <MDBModalFooter>
                <MDBBtn color="secondary" onClick={() => setShowAddUsersModal(false)}>Close</MDBBtn>
                <MDBBtn color="primary" onClick={handleAddUsers}><MDBIcon fas icon="plus-circle" />Users</MDBBtn>
            </MDBModalFooter>
        </MDBModalContent>
    </MDBModalDialog>
</MDBModal>

        </MDBContainer>
    );
}
